<!-- components/TransactionNotesDialog.vue -->
<template>
  <div class="transaction-notes-dialog">
    <div v-if="show" class="modal fade show" tabindex="-1" role="dialog" style="display: block;">
      <div class="modal-dialog modal-lg" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Transaction Notes - {{ transactionId }}</h5>
            <button type="button" class="btn-close" @click="close"></button>
          </div>
          <div class="modal-body">
            <div v-if="loading" class="text-center p-5">
              <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
            <div v-else>
              <!-- List of existing notes -->
              <div v-if="notes.length > 0" class="notes-list mb-4">
                <div v-for="note in notes" :key="note.id" class="note-item card mb-3">
                  <div class="card-body">
                    <div class="d-flex justify-content-between">
                      <h6 class="card-subtitle mb-2 text-muted">
                        {{ note.created_by }} - {{ formatDate(note.created_at) }}
                      </h6>
                    </div>
                    <p class="card-text whitespace-pre-line">{{ note.content }}</p>

                    <!-- Attachments -->
                    <div v-if="note.attachments && note.attachments.length > 0" class="attachments mt-2">
                      <h6>Attachments:</h6>
                      <div class="d-flex flex-wrap">
                        <div v-for="attachment in note.attachments" :key="attachment.id"
                             class="attachment-item me-3 mb-2">
                          <!-- Changed from <a> with href to <button> with click handler -->
                          <button
                            @click="downloadAttachment(attachment)"
                            class="btn btn-sm btn-outline-primary"
                            :disabled="downloadingAttachment === attachment.id"
                          >
                            {{ attachment.file_name }}
                            <span v-if="downloadingAttachment === attachment.id"
                                  class="spinner-border spinner-border-sm ms-2"
                                  role="status"
                                  aria-hidden="true"></span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="no-notes alert alert-info">
                No notes found for this transaction.
              </div>

              <!-- Form to add a new note -->
              <div class="add-note-form mt-4">
                <h5 class="mb-3">Add a Note</h5>
                <div class="mb-3">
                  <label for="noteContent" class="form-label">Note Content</label>
                  <textarea
                    id="noteContent"
                    v-model="newNote.content"
                    class="form-control"
                    rows="3"
                    placeholder="Enter your note here..."
                  ></textarea>
                </div>

                <div class="mb-3">
                  <label for="noteAttachment" class="form-label">Attachment (Optional)</label>
                  <input
                    type="file"
                    id="noteAttachment"
                    class="form-control"
                    @change="handleFileChange"
                  />
                  <div class="form-text">Only one attachment per note is currently supported</div>
                </div>

                <div v-if="selectedFile" class="selected-file mb-3">
                  <p class="mb-1">Selected File:</p>
                  <div class="card p-2 d-flex flex-row align-items-center">
                    {{ selectedFile.name }}
                    <button type="button" class="btn btn-sm btn-danger ms-auto" @click="removeFile">
                      Remove
                    </button>
                  </div>
                </div>

                <div class="text-end">
                  <button
                    type="button"
                    class="btn btn-primary"
                    @click="addNote"
                    :disabled="isAddingNote || !newNote.content"
                  >
                    <span v-if="isAddingNote" class="spinner-border spinner-border-sm me-1" role="status"></span>
                    Add Note
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="close">Close</button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="show" class="modal-backdrop fade show"></div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue';
import http from '@/services/http';

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  transactionId: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['close', 'noteAdded']);

const notes = ref([]);
const loading = ref(false);
const isAddingNote = ref(false);
const newNote = ref({
  content: ''
});
const selectedFile = ref(null);
const downloadingAttachment = ref(null); // Track which attachment is being downloaded

// Watch for transaction ID changes
watch(() => props.transactionId, (newId) => {
  if (newId && props.show) {
    loadNotes();
  }
});

// Watch for dialog visibility
watch(() => props.show, (isVisible) => {
  if (isVisible && props.transactionId) {
    loadNotes();
  } else {
    // Reset form when dialog is closed
    resetForm();
  }
});

// Load notes for the current transaction
async function loadNotes() {
  if (!props.transactionId) return;

  loading.value = true;
  try {
    const response = await http.get(`/api/transaction-monitoring/notes/${props.transactionId}`);
    notes.value = response.data?.message || [];
  } catch (error) {
    console.error('Failed to load notes', error);
  } finally {
    loading.value = false;
  }
}

// Download attachment via API instead of direct link
async function downloadAttachment(attachment) {
  downloadingAttachment.value = attachment.id;

  try {
    // Make API request to get the file content
    const response = await http.get(`/api/transaction-monitoring/attachments/${attachment.id}`, {
      responseType: 'blob' // Important: get response as blob
    });

    // Create a blob URL from the response data
    const blob = new Blob([response.data], {
      type: response.headers['content-type'] || 'application/octet-stream'
    });

    // Create a temporary URL for the blob
    const url = window.URL.createObjectURL(blob);

    // Create a hidden link element
    const link = document.createElement('a');
    link.href = url;
    link.download = attachment.file_name || 'download';
    link.style.display = 'none';

    // Add to document, click it, then remove it
    document.body.appendChild(link);
    link.click();

    // Clean up
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    }, 100);
  } catch (error) {
    console.error('Error downloading attachment:', error);
    alert('Error downloading attachment. Please try again.');
  } finally {
    downloadingAttachment.value = null;
  }
}

// Add a new note
async function addNote() {
  if (!newNote.value.content) return;

  isAddingNote.value = true;
  try {
    // Create a FormData object to properly handle file uploads
    const formData = new FormData();
    formData.append('transaction_id', props.transactionId);
    formData.append('content', newNote.value.content);

    // If we have a file, add it to the FormData
    if (selectedFile.value) {
      const fileData = await readFileAsBase64(selectedFile.value);
      const fileExtension = selectedFile.value.name.split('.').pop();

      // Add document properties as separate form fields
      formData.append('document.name', selectedFile.value.name);
      formData.append('document.extension', fileExtension);
      formData.append('document.content', fileData);
    }

    // Log what we're sending to help with debugging
    console.log('Sending transaction ID:', props.transactionId);
    console.log('Sending note content:', newNote.value.content);
    if (selectedFile.value) {
      console.log('Sending file with name:', selectedFile.value.name);
    }

    // Send the request with the appropriate content type header
    await http.post('/api/transaction-monitoring/notes', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    // Reload notes
    await loadNotes();

    // Reset the form
    resetForm();

    // Notify parent component
    emit('noteAdded');
  } catch (error) {
    console.error('Failed to add note', error);
    alert('Failed to add note. Please try again.');
  } finally {
    isAddingNote.value = false;
  }
}

// Alternative method using JSON with base64 encoded file
async function addNoteWithJsonPayload() {
  if (!newNote.value.content) return;

  isAddingNote.value = true;
  try {
    const payload = {
      transaction_id: props.transactionId,
      content: newNote.value.content
    };

    // If we have a file, add it to the payload
    if (selectedFile.value) {
      const fileData = await readFileAsBase64(selectedFile.value);
      const fileExtension = selectedFile.value.name.split('.').pop();

      payload.document = {
        name: selectedFile.value.name,
        extension: fileExtension,
        content: fileData
      };
    }

    console.log('Sending payload:', JSON.stringify(payload));

    // Send the request as JSON
    await http.post('/api/transaction-monitoring/notes', payload);

    // Reload notes
    await loadNotes();

    // Reset the form
    resetForm();

    // Notify parent component
    emit('noteAdded');
  } catch (error) {
    console.error('Failed to add note', error);
    alert('Failed to add note. Please try again.');
  } finally {
    isAddingNote.value = false;
  }
}

// Read file as base64
function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // Get the base64 string without the data URL prefix
      const base64String = reader.result.split(',')[1];
      resolve(base64String);
    };
    reader.onerror = error => reject(error);
    reader.readAsDataURL(file);
  });
}

// Handle file selection
function handleFileChange(event) {
  const file = event.target.files[0];
  if (!file) return;

  // Store the selected file
  selectedFile.value = file;
}

// Remove selected file
function removeFile() {
  selectedFile.value = null;
  // Reset file input by recreating it
  const fileInput = document.getElementById('noteAttachment');
  if (fileInput) {
    fileInput.value = '';
  }
}

// Reset the form
function resetForm() {
  newNote.value = {
    content: ''
  };
  selectedFile.value = null;
}

// Close the dialog
function close() {
  emit('close');
}

// Format a date string
function formatDate(dateString) {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString();
}

// Format file size
function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}
</script>

<style scoped>
.transaction-notes-dialog {
  position: relative;
  z-index: 1050;
}

.note-item {
  border-left: 4px solid #007bff;
}

.attachment-item {
  display: inline-block;
}

.whitespace-pre-line {
  white-space: pre-line;
}

.file-size {
  font-size: 0.8rem;
  opacity: 0.7;
}

.selected-file {
  display: flex;
  flex-direction: column;
}

.selected-file .card {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px 12px;
}
</style>